import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface EditPracticeFormProps {
  practice: Practice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { tinName: string; practiceAlias?: string }) => Promise<void>;
}

export function EditPracticeForm({ practice, open, onOpenChange, onSubmit }: EditPracticeFormProps) {
  const [tinName, setTinName] = useState('');
  const [practiceAlias, setPracticeAlias] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (practice) {
      setTinName(practice.tin_name);
      setPracticeAlias(practice.practice_alias || '');
    }
  }, [practice]);

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setError('');
        setLoading(false);
      }, 200);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onSubmit({ tinName, practiceAlias });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Practice</DialogTitle>
            <DialogDescription>
              Update the details for the <span className="font-semibold text-primary">{practice?.tin_name}</span> practice.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tinName" className="text-right">
                TIN Name
              </Label>
              <Input
                id="tinName"
                value={tinName}
                onChange={(e) => setTinName(e.target.value)}
                required
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="practiceAlias" className="text-right">
                Alias
              </Label>
              <Input
                id="practiceAlias"
                value={practiceAlias}
                onChange={(e) => setPracticeAlias(e.target.value)}
                className="col-span-3"
                placeholder="Optional Nickname"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
